// filepath: apps/web/src/features/admin/userRoles/UserRolesTable.tsx
//
// FR-UI-ADM-5 — table component for the user-role admin editor.
//
// Columns: checkbox (bulk-select), display_name, email,
// assigned_roles (pill list), sync_pending indicator, actions
// (open RolePickerPopover).
//
// Color is NOT the sole carrier of role identity (FR-UI-CORE-9
// AC-2): each role pill carries `data-role="<name>"` so the
// visual state is non-color-encoded. Pills are rendered as text
// nodes (INV-9: no dangerouslySetInnerHTML).

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { KeycloakUser } from "./types";
import { RolePickerPopover } from "./RolePickerPopover";

interface UserRolesTableProps {
  users: KeycloakUser[];
  selected: Set<string>;
  onSelectionChange: (id: string, checked: boolean) => void;
  currentVersion: number;
  /** Test seam — pinned in the a11y gate. */
  "data-testid"?: string;
}

export function UserRolesTable({
  users,
  selected,
  onSelectionChange,
  currentVersion,
  ...rest
}: UserRolesTableProps): React.ReactElement {
  const { t } = useTranslation();
  const [openPopoverUserId, setOpenPopoverUserId] = useState<string | null>(
    null,
  );

  return (
    <table data-testid={rest["data-testid"]} role="grid">
      <thead>
        <tr>
          <th>
            <input
              type="checkbox"
              aria-label={t("admin.users.column.select_all")}
              data-testid="user-roles-select-all"
              onChange={(e) => {
                users.forEach((u) => onSelectionChange(u.id, e.target.checked));
              }}
            />
          </th>
          <th>{t("admin.users.column.display_name")}</th>
          <th>{t("admin.users.column.email")}</th>
          <th>{t("admin.users.column.assigned_roles")}</th>
          <th>{t("admin.users.column.actions")}</th>
        </tr>
      </thead>
      <tbody>
        {users.length === 0 ? (
          <tr>
            <td colSpan={5} data-state="empty">
              {t("admin.users.empty.message")}
            </td>
          </tr>
        ) : null}
        {users.map((u) => (
          <tr key={u.id} data-user-id={u.id}>
            <td>
              <input
                type="checkbox"
                aria-label={t("admin.users.column.select_user", {
                  name: u.display_name,
                })}
                data-testid={`user-roles-checkbox-${u.id}`}
                checked={selected.has(u.id)}
                onChange={(e) => onSelectionChange(u.id, e.target.checked)}
              />
            </td>
            <td>{u.display_name || u.username}</td>
            <td>{u.email}</td>
            <td>
              <ul
                data-testid={`user-roles-pills-${u.id}`}
                aria-label={t("admin.users.column.assigned_roles")}
              >
                {u.assigned_roles.length === 0 ? (
                  <li data-state="no-roles">—</li>
                ) : null}
                {u.assigned_roles.map((role) => (
                  <li key={role} data-role={role}>
                    {role}
                  </li>
                ))}
              </ul>
            </td>
            <td>
              <button
                type="button"
                aria-label={t("admin.users.action.assign_roles_for", {
                  name: u.display_name,
                })}
                data-testid={`user-roles-edit-${u.id}`}
                onClick={() => setOpenPopoverUserId(u.id)}
              >
                {t("admin.users.action.assign_roles")}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={5} data-state="bulk-action-bar">
            {selected.size > 0
              ? t("admin.users.bulk.selected_count", { count: selected.size })
              : null}
          </td>
        </tr>
      </tfoot>

      {openPopoverUserId ? (
        <RolePickerPopover
          userId={openPopoverUserId}
          currentRoles={
            users.find((u) => u.id === openPopoverUserId)?.assigned_roles ?? []
          }
          currentVersion={currentVersion}
          onClose={() => setOpenPopoverUserId(null)}
        />
      ) : null}
    </table>
  );
}
